/**
 * CAOS-78 — the Setup app's DOMAINS surface: a customer's own domain on their Site, and the DNS
 * records they have to add before it can serve.
 *
 * ── WHY THIS SCREEN IS THE ACCEPTANCE CRITERION AND NOT A NICETY ─────────────────────────────
 * The ticket's third criterion is that the verification steps are shown IN-PRODUCT. That is not a
 * presentation preference: every remaining step in attaching a custom domain happens in a DNS zone
 * this platform does not control, so the only thing the product can do is tell one person exactly
 * which records to create. A domain that is verifiable through the API and nowhere else is a domain
 * nobody verifies — the owner is an administrator, not a caller, and they have no way to discover
 * a TXT record they were never shown.
 *
 * The records are rendered VERBATIM, and that is deliberate. Each one carries a token Cloudflare
 * generated; a value that is abbreviated, wrapped, case-folded or trimmed in the name of looking
 * tidy is a value that does not verify, and the failure is indistinguishable from the owner never
 * having added it. So the value is shown whole, in a copyable field, exactly as the API returned
 * it, and nothing here reformats it.
 *
 * ── WHAT IT SHOWS WHEN VERIFICATION IS NOT AVAILABLE ─────────────────────────────────────────
 * Cloudflare for SaaS is a paid add-on this platform does not have enabled yet, so `sites domains`
 * answers `verificationAvailable: false`. The screen says so, plainly, instead of drawing an
 * indefinite "verifying" state. A domain can still be claimed — that is what reserves the name —
 * and the claim is reported as exactly that: reserved, not pending.
 *
 * ── THE SHAPE, WHICH MATCHES THE REST OF SETUP ───────────────────────────────────────────────
 * A thin route-shape bridge over kernel verbs, the same way `seam-order.ts` is: it reads the org's
 * Sites from `metadata retrieve` (which is how any surface learns what components exist), then
 * reads and writes domains through the four `sites` verbs. Every fetch is fail-soft — a failure
 * paints a real message, never a crash and never a blank pane.
 */

export type Navigate = (href: string) => void;

export interface MetaResponse {
  json(): Promise<unknown>;
}
export type MetaFetch = (path: string, init?: RequestInit) => Promise<MetaResponse>;

const defaultMetaFetch: MetaFetch = (path, init) =>
  fetch(path, { credentials: 'same-origin', ...init }) as unknown as Promise<MetaResponse>;

let metaFetch: MetaFetch = defaultMetaFetch;

/** Override the API seam (production wiring / deterministic tests). */
export function setSetupDomainsFetch(fn: MetaFetch): void {
  metaFetch = fn;
}

interface Envelope {
  ok?: boolean;
  data?: unknown;
  error?: { message?: unknown; code?: unknown };
}

/** One DNS record the owner must create, as `custom-hostname-status.ts` describes it. */
interface VerificationStep {
  purpose?: string;
  recordType?: string;
  name?: string;
  value?: string;
  instruction?: string;
}

interface DomainView {
  hostname?: string;
  state?: string;
  servable?: boolean;
  summary?: string;
  steps?: VerificationStep[];
  errors?: string[];
}

interface DomainsResponse {
  domains: DomainView[];
  edgeTarget: string;
  verificationAvailable: boolean;
}

interface SiteRef {
  key: string;
  label: string;
}

/** POST one kernel verb. Never throws — the caller paints whatever comes back. */
async function post(path: string, body: unknown): Promise<{ ok: true; data: unknown } | { ok: false; message: string }> {
  let env: Envelope;
  try {
    const res = await metaFetch(path, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    env = (await res.json()) as Envelope;
  } catch {
    return { ok: false, message: 'The server could not be reached. Please try again.' };
  }
  if (env?.ok !== true) {
    const message = typeof env?.error?.message === 'string' ? env.error.message : 'The request was refused.';
    return { ok: false, message };
  }
  return { ok: true, data: env.data };
}

function humanize(key: string): string {
  return key.replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()).trim();
}

/**
 * The org's Sites, by component key.
 *
 * `metadata retrieve` is the read every Setup surface uses to learn what components an org has,
 * and it returns type/key/body with NO id — which is exactly why the `sites` verbs address a Site
 * by key. A screen cannot name a Site by something it is never told.
 */
async function fetchSites(): Promise<SiteRef[] | null> {
  let env: Envelope;
  try {
    const res = await metaFetch('/api/metadata/retrieve?type=site', { method: 'GET' });
    env = (await res.json()) as Envelope;
  } catch {
    return null;
  }
  if (env?.ok !== true) return null;
  const components = (env.data as { components?: unknown } | null)?.components;
  if (!Array.isArray(components)) return null;
  return components
    .filter((c): c is { key: string; body?: Record<string, unknown> } => typeof (c as { key?: unknown })?.key === 'string')
    .map((c) => {
      const label = c.body?.['label'];
      return { key: c.key, label: typeof label === 'string' && label.trim() ? label : humanize(c.key) };
    });
}

// ── DOM helpers, mirroring seam-order.ts ────────────────────────────────────────────────────────

function button(label: string, opts: { variant?: string } = {}): HTMLElement {
  const b = document.createElement('caos-button');
  if (opts.variant) b.setAttribute('variant', opts.variant);
  b.setAttribute('size', 'sm');
  b.setAttribute('type', 'button');
  b.textContent = label;
  return b;
}

function banner(kind: 'error' | 'status' | 'notice', message: string): HTMLElement {
  const el = document.createElement('div');
  el.className = `setup-domains-${kind}`;
  el.setAttribute('role', kind === 'error' ? 'alert' : 'status');
  el.textContent = message;
  return el;
}

function header(): HTMLElement {
  const head = document.createElement('caos-record-header');
  head.setAttribute('object-label', 'Setup · Company');
  head.setAttribute('heading', 'Domains');
  head.setAttribute('no-icon', '');
  return head;
}

function emptyState(title: string, detail: string): HTMLElement {
  const el = document.createElement('caos-empty-state');
  el.setAttribute('title', title);
  el.textContent = detail;
  return el;
}

/**
 * One DNS record, as a row the owner can copy out of.
 *
 * The value is a readonly input rather than text: a token is long, it must be copied exactly, and
 * selecting wrapped text out of a paragraph is where a trailing space comes from. `readonly`
 * rather than `disabled` because a disabled field cannot be selected at all.
 */
function stepRow(step: VerificationStep): HTMLElement {
  const row = document.createElement('div');
  row.className = 'setup-domains-step';

  const what = document.createElement('p');
  what.className = 'setup-domains-step-instruction';
  what.textContent = step.instruction ?? '';
  row.append(what);

  const grid = document.createElement('dl');
  grid.className = 'setup-domains-step-record';
  for (const [term, value] of [
    ['Type', step.recordType ?? ''],
    ['Name', step.name ?? ''],
    ['Value', step.value ?? ''],
  ] as const) {
    const dt = document.createElement('dt');
    dt.textContent = term;
    const dd = document.createElement('dd');
    if (term === 'Type') {
      dd.textContent = value;
    } else {
      // Verbatim, and copyable. See the module note on why nothing here reformats a token.
      const field = document.createElement('input');
      field.type = 'text';
      field.readOnly = true;
      field.value = value;
      field.className = 'setup-domains-copyable';
      field.setAttribute('aria-label', `${term} of the ${step.recordType ?? 'DNS'} record`);
      dd.append(field);
    }
    grid.append(dt, dd);
  }
  row.append(grid);
  return row;
}

/** The word the screen uses for a state, chosen so each one names who acts next. */
function stateLabel(state: string | undefined, verificationAvailable: boolean, servable: boolean): string {
  // Checked BEFORE the state, and this is the load-bearing line on the screen today. With no
  // Cloudflare configured the API still derives a state from the facts it has, and for a claimed
  // domain that reads `verifying` — which is true of the data and false of the world: nothing is
  // verifying it and nothing will. Every progress word is dishonest while the machine that would
  // produce the progress is absent, so none of them is used.
  if (!servable && !verificationAvailable) return 'Reserved — verification not enabled';

  switch (state) {
    case 'active':
      return 'Live';
    case 'awaiting_dns':
      return 'Waiting for your DNS records';
    case 'verifying':
      return 'Verifying — nothing to do';
    case 'failed':
      return 'Verification failed';
    case 'removed':
      return 'No longer attached';
    default:
      return 'Not yet verified';
  }
}

function domainCard(
  domain: DomainView,
  res: DomainsResponse,
  actions: { check: () => void; detach: () => void },
): HTMLElement {
  const card = document.createElement('section');
  card.className = 'setup-domains-card';

  const head = document.createElement('div');
  head.className = 'setup-domains-card-head';

  const name = document.createElement('h3');
  name.className = 'setup-domains-hostname';
  name.textContent = domain.hostname ?? '';

  const badge = document.createElement('span');
  badge.className = `setup-domains-badge is-${domain.servable ? 'live' : (domain.state ?? 'pending')}`;
  badge.textContent = stateLabel(domain.state, res.verificationAvailable, domain.servable === true);
  head.append(name, badge);
  card.append(head);

  if (domain.summary) {
    const summary = document.createElement('p');
    summary.className = 'setup-domains-summary';
    summary.textContent = domain.summary;
    card.append(summary);
  }

  const steps = domain.steps ?? [];
  if (steps.length > 0) {
    const heading = document.createElement('h4');
    heading.className = 'setup-domains-steps-heading';
    heading.textContent =
      steps.length === 1 ? 'Add this record to your DNS' : `Add these ${steps.length} records to your DNS`;
    card.append(heading);
    for (const step of steps) card.append(stepRow(step));
  } else if (!domain.servable && !res.verificationAvailable) {
    // No steps AND nothing is going to produce any. Said explicitly, because an empty step list
    // otherwise reads as "nothing left to do" on a domain that is not being served.
    card.append(
      banner(
        'notice',
        `This domain is reserved for your org. The records needed to verify it will be shown here once ` +
          `domain verification is enabled on this platform. Point ${domain.hostname ?? 'it'} at ` +
          `${res.edgeTarget} with a CNAME record in the meantime.`,
      ),
    );
  }

  for (const message of domain.errors ?? []) card.append(banner('error', message));

  const row = document.createElement('div');
  row.className = 'setup-domains-card-actions';
  if (res.verificationAvailable) {
    const check = button('Check now');
    check.addEventListener('click', actions.check);
    row.append(check);
  }
  const remove = button('Remove domain', { variant: 'ghost' });
  remove.addEventListener('click', actions.detach);
  row.append(remove);
  card.append(row);

  return card;
}

export interface SetupDomainsContext {
  readonly navigate?: Navigate;
  /** `/setup/domains[/<siteKey>]` — the trailing segment selects a Site. */
  readonly pathname?: string;
}

/** The Site the path names, if any. */
function siteKeyFromPath(pathname: string | undefined): string | null {
  const segments = (pathname ?? '').split('/').filter(Boolean);
  const at = segments.indexOf('domains');
  const next = at >= 0 ? segments[at + 1] : undefined;
  return next ? decodeURIComponent(next) : null;
}

/**
 * Render the Domains surface. Paints a loading pane immediately, reads the org's Sites, then the
 * selected Site's domains. Returns `{ root, done }` — the shape every Setup surface uses — so a
 * test can await the full round trip before asserting.
 */
export function renderSetupDomains(ctx: SetupDomainsContext = {}): { root: HTMLElement; done: Promise<void> } {
  const root = document.createElement('div');
  root.className = 'setup-pane setup-domains';
  root.append(emptyState('Loading…', ''));

  const done = (async (): Promise<void> => {
    const sites = await fetchSites();
    if (!sites) {
      root.replaceChildren(header(), banner('error', 'Could not read this org’s Sites. Please try again.'));
      return;
    }
    if (sites.length === 0) {
      root.replaceChildren(
        header(),
        emptyState(
          'No Sites yet',
          'A custom domain serves a Site, so there is nothing to attach one to until this org has one.',
        ),
      );
      return;
    }

    const requested = siteKeyFromPath(ctx.pathname);
    const selected = sites.find((s) => s.key === requested) ?? sites[0]!;

    const repaint = async (): Promise<void> => {
      const result = await post('/api/sites/domains', { siteKey: selected.key });
      if (!result.ok) {
        root.replaceChildren(header(), banner('error', result.message));
        return;
      }
      paint(root, selected, sites, result.data as DomainsResponse, ctx, repaint);
    };
    await repaint();
  })();

  return { root, done };
}

function paint(
  root: HTMLElement,
  selected: SiteRef,
  sites: SiteRef[],
  res: DomainsResponse,
  ctx: SetupDomainsContext,
  repaint: () => Promise<void>,
): void {
  root.replaceChildren(header());

  const intro = document.createElement('p');
  intro.className = 'setup-domains-intro';
  intro.textContent =
    `Put one of your own domains in front of a Site. You add the DNS records shown here in your ` +
    `own domain's DNS; nothing can be verified until they exist, because the zone is yours and not ours.`;
  root.append(intro);

  if (!res.verificationAvailable) {
    root.append(
      banner(
        'notice',
        'Domain verification is not enabled on this platform yet. You can reserve a domain now — that ' +
          'holds the name for your org — but it cannot be verified or served until it is.',
      ),
    );
  }

  // The Site picker is drawn only when there is a choice to make. One Site and a picker is a
  // control that cannot do anything.
  if (sites.length > 1) {
    const picker = document.createElement('label');
    picker.className = 'setup-domains-site-picker';
    picker.textContent = 'Site';
    const select = document.createElement('select');
    for (const site of sites) {
      const option = document.createElement('option');
      option.value = site.key;
      option.textContent = site.label;
      if (site.key === selected.key) option.selected = true;
      select.append(option);
    }
    select.addEventListener('change', () => {
      ctx.navigate?.(`/setup/domains/${encodeURIComponent(select.value)}`);
    });
    picker.append(select);
    root.append(picker);
  }

  const banners = document.createElement('div');
  banners.className = 'setup-domains-banners';
  root.append(banners);

  const domains = res.domains ?? [];
  if (domains.length === 0) {
    root.append(
      emptyState('No domains yet', `${selected.label} is reached at its platform address until you attach one.`),
    );
  }
  for (const domain of domains) {
    root.append(
      domainCard(domain, res, {
        check: () => {
          void act('/api/sites/check-domain', { hostname: domain.hostname }, banners, repaint);
        },
        detach: () => {
          void act('/api/sites/detach-domain', { hostname: domain.hostname }, banners, repaint);
        },
      }),
    );
  }

  // ── attach ────────────────────────────────────────────────────────────────────────────────
  const form = document.createElement('div');
  form.className = 'setup-domains-attach';

  const label = document.createElement('label');
  label.className = 'setup-domains-attach-label';
  label.textContent = 'Add a domain';
  const input = document.createElement('input');
  input.type = 'text';
  input.placeholder = 'www.example.com';
  input.className = 'setup-domains-attach-input';
  input.setAttribute('aria-label', 'Domain to attach');
  label.append(input);

  const add = button('Attach domain', { variant: 'primary' });
  add.addEventListener('click', () => {
    const hostname = input.value.trim();
    if (!hostname) {
      banners.replaceChildren(banner('error', 'Enter the domain you want to attach.'));
      return;
    }
    void act('/api/sites/attach-domain', { siteKey: selected.key, hostname }, banners, repaint);
  });

  const hint = document.createElement('p');
  hint.className = 'setup-domains-attach-hint';
  hint.textContent = `Every domain you attach must point at ${res.edgeTarget} with a CNAME record.`;

  form.append(label, add, hint);
  root.append(form);
}

/** Run one write, report what it said, and repaint from the server rather than from a guess. */
async function act(
  path: string,
  body: unknown,
  banners: HTMLElement,
  repaint: () => Promise<void>,
): Promise<void> {
  const result = await post(path, body);
  if (!result.ok) {
    banners.replaceChildren(banner('error', result.message));
    return;
  }
  // Re-read rather than patching the pane from the response: the row the server now holds is the
  // only thing that decides whether a domain is served, and a screen that painted its own optimistic
  // version of that would tell the owner a domain is live while the router disagrees.
  await repaint();
}
